import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchGroupPayPage, startGroupCheckout } from "@/lib/payApi";
import { formatDisplayDate, formatTime } from "@/lib/bookingLabels";
import { ArrowLeft, FileDown } from "lucide-react";
import { buildInvoicePdf, downloadBlob } from "@/lib/invoice";
import { formatGbpFromPence, type GroupPayRow } from "@/types/database";

const BookingPayGroup = () => {
  const { token = "" } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const status = params.get("status");
  const [rows, setRows] = useState<GroupPayRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = () => {
    if (location.key !== "default") navigate(-1);
    else navigate("/");
  };

  const load = useCallback(async () => {
    const res = await fetchGroupPayPage(token);
    if (res.ok === false) setError(res.error);
    else setRows(res.data);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    document.title = "Pay securely · Olivia Flemming";
    void load();
  }, [load]);

  // Poll briefly after Stripe success redirect while the payment confirms
  useEffect(() => {
    if (status !== "success") return;
    let tries = 0;
    const id = window.setInterval(() => {
      tries += 1;
      void load();
      if (tries >= 10) window.clearInterval(id);
    }, 2500);
    return () => window.clearInterval(id);
  }, [status, load]);

  const unpaid = rows.filter(
    (r) => r.booking_status === "scheduled" && r.payment_status !== "paid" && r.payment_status !== "not_required"
  );
  const total = unpaid.reduce((s, r) => s + r.fee_pence, 0);
  const blockTotal = rows.filter((r) => r.booking_status !== "cancelled").reduce((s, r) => s + r.fee_pence, 0);

  const payAll = async () => {
    setPaying(true);
    setError(null);
    const res = await startGroupCheckout(token);
    if (res.ok === true) window.location.href = res.data.url;
    else {
      setError(res.error);
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10">
        <div className="max-w-xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="font-playfair tracking-widest text-sm">
            OLIVIA FLEMMING
          </Link>
          <span className="text-[10px] uppercase tracking-widest text-white/40 font-inter">
            Secure payment
          </span>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4 py-10 md:py-14 space-y-6">
        <button
          type="button"
          onClick={goBack}
          className="mb-1 inline-flex items-center gap-2 text-sm font-inter text-white/50 hover:text-primary transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {loading ? (
          <p className="text-white/50 text-sm font-inter text-center">Loading bookings…</p>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6 text-center">
            <h1 className="font-playfair text-2xl mb-3">Link unavailable</h1>
            <p className="text-sm text-white/70 font-inter">{error}</p>
          </div>
        ) : (
          <>
            <div className="text-center">
              <p className="text-primary text-xs tracking-[0.3em] uppercase mb-3 font-inter">
                {rows[0].venue_or_client_name}
              </p>
              <h1 className="font-playfair text-3xl md:text-4xl">
                {unpaid.length === 0 ? "All paid. Thank you" : "Your bookings"}
              </h1>
              <p className="text-white/50 text-sm font-inter mt-2">
                Pay for everything at once, or each performance on its own. Each one has its own invoice too.
              </p>
            </div>

            <ul className="space-y-3">
              {rows.map((r) => {
                const paid = r.payment_status === "paid";
                const cancelled = r.booking_status === "cancelled";
                return (
                  <li
                    key={r.reference}
                    className="rounded-2xl border border-white/10 bg-secondary/60 p-5 flex items-center justify-between gap-4 font-inter"
                  >
                    <div className="min-w-0">
                      <p className="text-white">{formatDisplayDate(r.performance_date)}</p>
                      <p className="text-xs text-white/50 mt-1">
                        {r.start_time ? `${formatTime(r.start_time)}${r.end_time ? ` to ${formatTime(r.end_time)}` : ""} · ` : ""}{r.reference}
                      </p>
                      {(r.fuel_charge_pence ?? 0) > 0 && (
                        <p className="text-xs text-white/40 mt-0.5">Includes {formatGbpFromPence(r.fuel_charge_pence ?? 0)} travel</p>
                      )}
                      {r.due_date && (
                        <p className="text-xs text-primary/80 mt-0.5">
                          Payment due by {formatDisplayDate(r.due_date)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 shrink-0 flex-wrap justify-end">
                      <button
                        type="button"
                        aria-label={`Download invoice ${r.reference}`}
                        onClick={() =>
                          downloadBlob(
                            buildInvoicePdf({
                              invoiceNo: r.reference,
                              clientName: r.venue_or_client_name,
                              contactName: r.contact_name,
                              clientEmail: r.client_email,
                              dueDate: r.due_date,
                              lines: [{ ...r, address: [r.address, r.venue_postcode].filter(Boolean).join(", "), paid: r.payment_status === "paid" }],
                            }),
                            `Invoice ${r.reference}.pdf`
                          )
                        }
                        className="rounded-full border border-white/15 p-2 text-white/60 hover:text-primary hover:border-primary"
                      >
                        <FileDown className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-sm">{formatGbpFromPence(r.fee_pence)}</span>
                      {paid ? (
                        <span className="text-xs uppercase tracking-widest text-primary">Paid</span>
                      ) : cancelled ? (
                        <span className="text-xs uppercase tracking-widest text-white/40">Cancelled</span>
                      ) : (
                        <Link
                          to={`/booking/pay/${r.pay_token}`}
                          className="rounded-full border border-primary/50 text-primary px-4 py-1.5 text-xs uppercase tracking-widest hover:bg-primary/10"
                        >
                          Pay this
                        </Link>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="rounded-2xl border border-white/10 bg-secondary/60 p-5 font-inter text-sm space-y-2">
              <div className="flex justify-between text-white/70"><span>Total for all performances</span><span>{formatGbpFromPence(blockTotal)}</span></div>
              {blockTotal !== total && <div className="flex justify-between text-white/50"><span>Already paid</span><span>{formatGbpFromPence(blockTotal - total)}</span></div>}
              <div className="pt-2 border-t border-white/10 flex items-end justify-between">
                <span className="text-white/45 text-xs uppercase tracking-wider">Left to pay</span>
                <span className="font-playfair text-3xl">{formatGbpFromPence(total)}</span>
              </div>
            </div>

            {unpaid.length > 1 && (
              <button
                type="button"
                onClick={payAll}
                disabled={paying}
                className="w-full rounded-full bg-primary text-primary-foreground font-medium py-4 text-sm hover:opacity-90 disabled:opacity-50"
              >
                {paying ? "Opening checkout…" : `Pay all ${unpaid.length} together · ${formatGbpFromPence(total)}`}
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                downloadBlob(
                  buildInvoicePdf({
                    invoiceNo: `${rows[0].reference} (${rows.length} dates)`,
                    clientName: rows[0].venue_or_client_name,
                    contactName: rows[0].contact_name,
                    clientEmail: rows[0].client_email,
                    lines: rows
                      .filter((r) => r.booking_status !== "cancelled")
                      .map((r) => ({ ...r, address: [r.address, r.venue_postcode].filter(Boolean).join(", "), paid: r.payment_status === "paid" })),
                  }),
                  `Invoice ${rows[0].reference}.pdf`
                )
              }
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/20 py-3.5 text-sm font-inter text-white/80 hover:border-primary hover:text-primary transition-colors"
            >
              <FileDown className="w-4 h-4" /> Download one invoice for everything (PDF)
            </button>
            {error && <p className="text-sm text-red-300 font-inter text-center">{error}</p>}
          </>
        )}
      </main>
    </div>
  );
};

export default BookingPayGroup;
